/**
 * Support widget — the tracking vocabulary (#5619).
 *
 * The Tracking Events Dictionary is one vocabulary for every support surface, but it
 * was written down twice: as TypeScript enums in fe-react and as bare string literals
 * in the Django adapter. Two surfaces reporting `surface`/`channel` values that differ
 * by a character produce two funnels that silently never join, and nothing fails.
 *
 * Const objects rather than `enum`s so the Django adapter — which has no PostHog
 * types of its own — can spread them straight into a property bag, and so each name
 * doubles as its own union type.
 */

/** Where the support entry point lives. One value per surface, never reused. */
export const SupportSurface = {
  BSDIR_PAGE: "bsdir_page",
  EXIT_INTENT_JOB_FORM: "exit_intent_job_form",
  JOB_LIST: "job_list",
  JOB_PAGE: "job_page",
  PROS_PROFILE: "pros_profile",
  PROS_LIST: "pros_list"
} as const
export type SupportSurface = (typeof SupportSurface)[keyof typeof SupportSurface]

/** What made the entry point appear. */
export const SupportTrigger = {
  TIMER: "timer",
  /** Shown on every visit, not after a configured wait (job-posted flow, #5620). */
  ALWAYS_VISIBLE: "always_visible",
  /** The customer pressed the job form's close control. */
  EXIT_CLOSE: "exit_close",
  /** Reserved: a desktop pointer leaving the viewport. Not wired yet. */
  EXIT_MOUSEOUT: "exit_mouseout"
} as const
export type SupportTrigger = (typeof SupportTrigger)[keyof typeof SupportTrigger]

/** The channel the customer picked. Mirrors the endpoint's `channels` keys. */
export const SupportChannel = {
  CHAT: "chat",
  CALL: "call",
  CONTACT_FORM: "contact_form"
} as const
export type SupportChannel = (typeof SupportChannel)[keyof typeof SupportChannel]

/**
 * How a surface was closed without a channel being picked.
 *
 * `DismissMethod` in `types.ts` is an alias of this, so the core's `closeSheet(method)`
 * and the dictionary cannot drift.
 */
export const SupportDismissMethod = {
  CLOSE_X: "close_x",
  BACKDROP: "backdrop",
  ESC: "esc",
  AUTO_HIDE: "auto_hide",
  /** Exit intent: the customer chose to carry on with the job form. */
  CONTINUE_JOB: "continue_job",
  /** Exit intent: the customer abandoned the job form. */
  CLOSE_FORM: "close_form"
} as const
export type SupportDismissMethod = (typeof SupportDismissMethod)[keyof typeof SupportDismissMethod]

/**
 * The core's own internal events — what `mountSupportWidget` passes to `track`. Not the
 * dictionary's event names: {@link routeSupportCoreEvent} maps these onto those.
 */
export const SupportCoreEvent = {
  REVEALED: "support_widget_revealed",
  OPENED: "support_widget_opened",
  CLOSED: "support_widget_closed",
  DISMISSED: "support_widget_dismissed",
  CHAT: "support_widget_chat",
  PHONE: "support_widget_phone",
  FORM: "support_widget_form"
} as const
export type SupportCoreEvent = (typeof SupportCoreEvent)[keyof typeof SupportCoreEvent]

/** `support_widget_closed` payload: how the sheet closed and whether a channel was picked first. */
export interface SupportClosedPayload {
  method?: SupportDismissMethod
  secondsOpen?: number
  channelSelected?: boolean
}

/** `support_widget_dismissed` payload: the whole widget was hidden. */
export interface SupportDismissedPayload {
  reason?: string
  secondsOpen?: number
}

/**
 * The core's own internal event names → the dictionary's `channel` value. The core
 * stays framework-agnostic and emits one event per channel; every adapter has to make
 * the same mapping, so it lives here rather than in each of them.
 */
export const SUPPORT_CHANNEL_BY_CORE_EVENT: { [coreEvent: string]: SupportChannel } = {
  [SupportCoreEvent.CHAT]: SupportChannel.CHAT,
  [SupportCoreEvent.PHONE]: SupportChannel.CALL,
  [SupportCoreEvent.FORM]: SupportChannel.CONTACT_FORM
}

/**
 * Where each dictionary event goes on a surface. The surface owns the property bag and
 * the capture call; it also owns the once-per-session guard for `shown`, since the
 * surface name it keys on is the surface's to know.
 */
export interface SupportTrackingSinks {
  shown: () => void
  clicked: () => void
  channelSelected: (channel: SupportChannel) => void
  dismissed: (method: SupportDismissMethod, secondsOpen: number) => void
}

/**
 * Map one core event onto the four dictionary events (#5619). Every surface used to
 * spell this mapping out itself; a rule changed in one copy (what counts as a
 * dismissal, the default method) is a funnel that silently stops joining.
 */
export const routeSupportCoreEvent = (event: string, data: object | undefined, sinks: SupportTrackingSinks): void => {
  if (event in SUPPORT_CHANNEL_BY_CORE_EVENT) {
    sinks.channelSelected(SUPPORT_CHANNEL_BY_CORE_EVENT[event])
    return
  }

  switch (event) {
    case SupportCoreEvent.REVEALED:
      sinks.shown()
      break
    case SupportCoreEvent.OPENED:
      sinks.clicked()
      break
    case SupportCoreEvent.CLOSED: {
      const closed = (data || {}) as SupportClosedPayload
      // A channel pick closes the sheet too, but that is NOT a dismissal (AC 2).
      if (!closed.channelSelected) {
        sinks.dismissed(closed.method || SupportDismissMethod.CLOSE_X, closed.secondsOpen || 0)
      }
      break
    }
    case SupportCoreEvent.DISMISSED: {
      const hidden = (data || {}) as SupportDismissedPayload
      sinks.dismissed(
        hidden.reason === "auto_hide" ? SupportDismissMethod.AUTO_HIDE : SupportDismissMethod.CLOSE_X,
        hidden.secondsOpen || 0
      )
      break
    }
    default:
  }
}
